import React from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { TableCell, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import useModalStore from "@/store/useModalStore";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import { Edit, Plus, Trash } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { OffersService } from "@/services/offers.service";
import CheckTable from "@/assets/icons/CheckTable";
import BadTable from "@/assets/icons/BadTable";
import CreateOffer from "./Partials/CreateOffer";
import EditOffer from "./Partials/EditOffer";
import Badge from "@/components/shared/Badge/Badge";
import { toast } from "@/hooks/use-toast";
import { AxiosError } from "axios";
import { AxiosErrorResponse } from "@/@types/Errors";
import Ticket from "@/assets/icons/Ticket";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import CTATable from "@/components/shared/TableListCustom/CTATable";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete";
import { ProductDetailsResponse, ProductRole } from "@/@types/Products";
import { usePermissions } from "@/auth/PermissionsContext";
import { PaymentMethodsIcons } from "./Partials/PaymentMethodsIcons";
import { OfferData, OffersResponse } from "@/@types/Products/Offers";
import { recurringIntervalSuffix } from "@/utils/formatOfferRecurringLabel";

export default function Offers() {
  const { modals, openModal, closeModal } = useModalStore();

  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["offers", "common"]);
  const [selectedOffer, setSelectedOffer] = React.useState<OfferData | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const { product } = useOutletContext<{ product: ProductDetailsResponse }>();
  const queryClient = useQueryClient();

  const { hasPermission } = usePermissions();
  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;
  const canCreateOffer = isProducer && hasPermission('offers.create_offer');
  const canEditOffer = isProducer && hasPermission('offers.edit_offer');
  const canDeactivateDeleteOffer = isProducer && hasPermission('offers.deactivate_delete_offer');

  const {
    data: offers,
    isPending: isLoadingOffers,
  } = useQuery({
    queryKey: [`offers-${id}`, currentPage],
    queryFn: () => {
      if (!id) {
        throw new Error("Product id is required");
      }

      return OffersService.getOffers({ productId: id, page: currentPage });
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 60 * 24,
  });

  const { mutateAsync: deleteOffer, isPending: isDeletingOffer } = useMutation({
    mutationFn: async () => {
      if (!selectedOffer?.id || !id) return;
      await OffersService.deleteOffer({
        offerId: selectedOffer.id,
        productId: id
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [`offers-${id}`] });

      const previousQueries = queryClient.getQueriesData({
        queryKey: [`offers-${id}`],
      });

      // DELETE desativa (soft), não apaga. Manter a linha e só marcar inativa.
      queryClient.setQueriesData(
        { queryKey: [`offers-${id}`], exact: false },
        (oldData: OffersResponse | undefined) => {
          if (!oldData || !selectedOffer?.id) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((offer) =>
              offer.id === selectedOffer.id
                ? { ...offer, is_active: false }
                : offer
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (error: AxiosError<AxiosErrorResponse>, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast({
        variant: "error",
        description:
          error?.response?.data?.message || t("common:data_submission_error"),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`offers-${id}`] });
      queryClient.invalidateQueries({ queryKey: [`checkouts-${id}`] });
      toast({ variant: "success", description: t("deactivate_offer") });
      closeModal("delete-offer");
    },
  });

  const headers = [
    { title: t("common:invoice"), key: "invoice" },
    { title: t("common:value"), key: "value" },
    { title: t("common:affiliates"), key: "affiliates" },
    { title: t("common:payment_methods"), key: "methods" },
    { title: t("common:status"), key: "status" },
    { title: "", key: "CTA" },
  ];

  return (
    <>
      {canCreateOffer && (
        <CustomSheet
          open={modals["create-offer"]}
          onClose={() => closeModal("create-offer")}
        >
          <CreateOffer
            categoryType={product.category.type}
            productName={product.name}
            modalName="create-offer"
          />
        </CustomSheet>
      )}
      {canEditOffer && (
        <CustomSheet
          open={modals["edit-offer"]}
          onClose={() => closeModal("edit-offer")}
        >
          <EditOffer
            offer={selectedOffer}
            categoryType={product.category.type}
            productName={product.name}
            modalName="edit-offer"
          />
        </CustomSheet>
      )}

      <Card className="w-full mt-8 overflow-x-auto">
        <div className="flex md:items-center flex-col gap-3 md:flex-row w-full p-4 justify-between">
          <h1 className="text-base font-semibold">{t("offers")}</h1>
          {canCreateOffer && (
            <Button
              data-cy="btn-create-offer"
              className="w-full md:max-w-[133px]"
              onClick={() => openModal("create-offer")}
            >
              <Plus />
              {t("create_offer")}
            </Button>
          )}
        </div>

        <TableListCustom
          emptyDescriptionMessage={t("emptyOffers.description")}
          emptyIconMessage={<Ticket />}
          emptyTitleMessage={t("emptyOffers.title")}
          headers={headers}
          isLoadingData={isLoadingOffers}
          data={offers ?? { data: [] }}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        >
          {offers?.data?.map((offer) => (
            <TableRow
              key={offer.id}
              className="h-[64px] border-b border-b-colorBorder hover:bg-transparent"
              data-cy="table-row"
            >
              <TableCell className="font-medium">
                {offer.display_name || offer.name}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <span>
                    {offer.price_formatted}
                    {recurringIntervalSuffix(
                      offer.payment_type,
                      offer.recurring_interval,
                    )}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                {offer.is_affiliate_enabled ? <CheckTable /> : <BadTable />}
              </TableCell>
              <TableCell>
                <PaymentMethodsIcons
                  offer={offer}
                  t={t}
                />
              </TableCell>
              <TableCell className="w-auto">
                <div className="max-w-[65px] inline-block">
                  <Badge status={offer?.is_active === false ? "inactive" : "active"} />
                </div>
              </TableCell>
              <TableCell>
                {(canEditOffer || canDeactivateDeleteOffer) && (
                  <CTATable
                    items={[
                      ...(canEditOffer ? [{
                        id: 1,
                        label: t("edit"),
                        icon: <Edit />,
                        action: () => {
                          setSelectedOffer(offer);
                          openModal("edit-offer");
                        },
                      }] : []),
                      ...(canDeactivateDeleteOffer
                        && offer.is_active !== false
                        && (offer.total_sales ?? 0) < 1
                        ? [{
                          id: 2,
                          label: t("deactivate_offer"),
                          icon: <Trash color="#F87171" />,
                          action: () => {
                            setSelectedOffer(offer);
                            openModal("delete-offer");
                          },
                        }]
                        : []),
                    ]}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
          <DialogDelete
            onClose={() => closeModal("delete-offer")}
            isOpen={modals["delete-offer"]}
            title={t("deactivate_offer_title")}
            description={t("deactivate_offer_description")}
            onDelete={async () => {
              await deleteOffer();
            }}
            isDeleting={isDeletingOffer}
          />
        </TableListCustom>
      </Card>
    </>
  );
}
